/**
 * `.pfai` ŞİFRELİ YEDEK — çalışma zamanı modülünün yüklenmesi.
 *
 * NİÇİN DİNAMİK IMPORT, `import { encryptPfai } from "@parafon/local"` DEĞİL:
 * `apps/web` yerel pakete PAKET olarak bağlı değil. Yerel paketin tarayıcı
 * çıktısı `/parafon-db/` altından statik dosya olarak sunuluyor ve arayüz onu
 * çalışma zamanında yüklüyor — `createRepository.ts` ve `createCentral.ts` ile
 * aynı desen, aynı gerekçe. Statik bir import Vite'ın paketine yerel paketin
 * bir KOPYASINI gömerdi ve o kopya `/parafon-db/` altındaki sürümle
 * ayrışabilirdi; şifreli yedek biçiminde bu ayrışma "dosya bir sürümle
 * yazılıp başka bir sürümle okunuyor" demek olurdu.
 *
 * KRİPTO CİHAZDA KALIYOR: bu modül tarayıcının `crypto.subtle`ını kullanıyor,
 * merkez API'sinin böyle bir uç noktası yok ve olmayacak. Parola ve düz metin
 * ağa hiç çıkmıyor.
 */

import { M } from "../dil";

const PFAI_MODULE_URL = "/parafon-db/index.js";

export interface PfaiHeaderView {
  readonly version: number;
  readonly createdAt: string;
  readonly kdf: { readonly iterations: number };
}

/** `.pfai` başlığının anahtar türetme kısmı (tuz + tur sayısı). */
export interface PfaiKdf {
  readonly name: "PBKDF2";
  readonly hash: "SHA-256";
  readonly iterations: number;
  readonly salt: string;
}

export interface BackupKeyView {
  readonly key: CryptoKey;
  readonly kdf: PfaiKdf;
}

interface PfaiModule {
  readonly encryptPfai: (plainJson: string, passphrase: string) => Promise<string>;
  readonly decryptPfai: (fileText: string, passphrase: string) => Promise<string>;
  readonly readPfaiHeader: (fileText: string) => PfaiHeaderView & { readonly kdf: PfaiKdf };
  readonly deriveBackupKey: (
    passphrase: string,
    opts?: { readonly salt?: string; readonly iterations?: number },
  ) => Promise<BackupKeyView>;
  readonly encryptPfaiWithKey: (plainJson: string, backup: BackupKeyView) => Promise<string>;
}

let cached: Promise<PfaiModule> | null = null;

function load(): Promise<PfaiModule> {
  cached ??= import(/* @vite-ignore */ PFAI_MODULE_URL).then((m: unknown) => {
    const mod = m as Partial<PfaiModule>;
    if (
      typeof mod.encryptPfai !== "function" ||
      typeof mod.decryptPfai !== "function" ||
      typeof mod.readPfaiHeader !== "function" ||
      typeof mod.deriveBackupKey !== "function" ||
      typeof mod.encryptPfaiWithKey !== "function"
    ) {
      // AÇIK HATA: modül yüklendi ama beklenen üyeler yok. Sessizce
      // "yedekleme çalışmıyor" demek yerine sebebi söylüyoruz — bu, eksik
      // dosyanın SPA kabuğuyla doldurulduğu sınıftan bir arıza olabilir
      // (canlıda `/parafon-db/index.js` tam bu şekilde eksik çıktı).
      throw new Error(M.yedek.kayit.modulUyumsuz);
    }
    return mod as PfaiModule;
  });
  return cached;
}

export async function encryptPfai(plainJson: string, passphrase: string): Promise<string> {
  return (await load()).encryptPfai(plainJson, passphrase);
}

export async function decryptPfai(fileText: string, passphrase: string): Promise<string> {
  return (await load()).decryptPfai(fileText, passphrase);
}

export async function readPfaiHeader(
  fileText: string,
): Promise<PfaiHeaderView & { readonly kdf: PfaiKdf }> {
  return (await load()).readPfaiHeader(fileText);
}

/** Y şeridi: parolayı bir kez anahtara çevirir (dışa çıkarılamaz). */
export async function deriveBackupKey(
  passphrase: string,
  opts?: { readonly salt?: string },
): Promise<BackupKeyView> {
  return (await load()).deriveBackupKey(passphrase, opts);
}

/** Y şeridi: saklanan anahtarla sıradan bir `.pfai` üretir. */
export async function encryptPfaiWithKey(plainJson: string, backup: BackupKeyView): Promise<string> {
  return (await load()).encryptPfaiWithKey(plainJson, backup);
}
